import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#applyGroupedRows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const groupLineKeys = this.#getGroupedLineKeys(lineItemRow);

    if (groupLineKeys.length > 1) {
      return this.updateGroupedQuantity({
        line,
        quantity,
        lineKeys: groupLineKeys,
        action: quantity === 0 ? 'clear' : 'change',
      });
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const groupLineKeys = this.#getGroupedLineKeys(cartItemRowToRemove);
    if (groupLineKeys.length > 1) {
      this.updateGroupedQuantity({
        line,
        quantity: 0,
        lineKeys: groupLineKeys,
        action: 'clear',
      });
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#queueApplyGroupedRows();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates grouped line items using cart_update_url and line item keys.
   * @param {{ line: number; quantity: number; lineKeys: string[]; action: string; }} config
   */
  updateGroupedQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, lineKeys } = config;
    const { cartTotal } = this.refs;

    /** @type {Record<string, number>} */
    const updates = {};
    lineKeys.forEach((lineKey, index) => {
      updates[lineKey] = index === 0 ? quantity : 0;
    });

    cartTotal?.shimmer();

    const body = JSON.stringify({
      updates,
      sections: Array.from(this.#getSectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#queueApplyGroupedRows();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#queueApplyGroupedRows();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #queueApplyGroupedRows() {
    requestAnimationFrame(() => {
      this.#applyGroupedRows();
      this.#updateCartQuantitySelectorButtonStates();
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  #getGroupedLineKeys(row) {
    return (row.dataset.groupLineKeys || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

	  #applyGroupedRows() {
	    const cartItemRows = Array.from(this.querySelectorAll('[ref="cartItemRows[]"]'));
	    if (cartItemRows.length === 0) return;

    /** @type {Map<string, HTMLTableRowElement[]>} */
    const groupedRows = new Map();

    for (const row of cartItemRows) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      if (row.dataset.groupable !== 'true') continue;
      if (row.dataset.parentKey) continue;

      const signature = row.dataset.groupSignature;
      if (!signature) continue;

      const rows = groupedRows.get(signature) || [];
      rows.push(row);
      groupedRows.set(signature, rows);
    }

	    groupedRows.forEach((rows) => {
	      if (rows.length < 2) return;

		      const primaryRow = this.#getPrimaryGroupRow(rows);
		      const totalQuantity = rows.reduce((sum, row) => sum + this.#getRowQuantity(row), 0);
		      const originalLinePrice = rows.reduce((sum, row) => sum + this.#getRowOriginalPrice(row), 0);
		      const finalLinePrice = rows.reduce((sum, row) => sum + this.#getRowFinalPrice(row), 0);
	      const freeQuantity = rows.reduce((sum, row) => {
	        return sum + (this.#isFreeRow(row) ? this.#getRowQuantity(row) : 0);
	      }, 0);
		      const hasDiscount = originalLinePrice > finalLinePrice;
		      const discountPercent = originalLinePrice
		        ? Math.round(((originalLinePrice - finalLinePrice) / originalLinePrice) * 100)
		        : 0;

	      const discountTitles = this.#getGroupedDiscountTitles(rows);

	      primaryRow.dataset.groupActive = 'true';
      primaryRow.dataset.groupLineKeys = rows
        .slice()
        .sort((left, right) => {
          if (left === primaryRow) return -1;
          if (right === primaryRow) return 1;
          return 0;
        })
        .map((row) => row.dataset.key || '')
        .filter(Boolean)
        .join(',');

		      this.#updateGroupedPrimaryRow(primaryRow, {
		        totalQuantity,
		        freeQuantity,
		        hasDiscount,
		        discountPercent,
		        discountTitles,
		      });

      rows.forEach((row) => {
        if (row === primaryRow) return;
        row.hidden = true;
        row.setAttribute('aria-hidden', 'true');
      });
    });
  }

  #getPrimaryGroupRow(rows) {
    return (
      rows
        .slice()
        .sort((left, right) => this.#getRowFinalPrice(right) - this.#getRowFinalPrice(left))[0] || rows[0]
    );
  }

	  #updateGroupedPrimaryRow(row, groupState) {
    const quantityInput = row.querySelector('input[data-cart-line]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = groupState.totalQuantity.toString();
      quantityInput.defaultValue = groupState.totalQuantity.toString();
    }

    const groupNote = row.querySelector('[data-group-note]');
    if (!(groupNote instanceof HTMLElement)) return;

    const noteText = this.#getGroupNoteText(groupState);
    groupNote.textContent = noteText;
    groupNote.classList.toggle('hidden', noteText.length === 0);
  }

		  #getGroupNoteText(groupState) {
	    if (typeof groupState.discountTitles === 'string' && groupState.discountTitles.length > 0) {
	      const prefix = Theme.translations.cart_group_discount_active || '';
	      return prefix ? `${prefix}: ${groupState.discountTitles}` : groupState.discountTitles;
	    }

		    if (groupState.freeQuantity > 0) {
	      if (groupState.freeQuantity === 1 && Theme.translations.cart_group_free_quantity_one) {
	        return Theme.translations.cart_group_free_quantity_one;
	      }

      const otherTemplate = Theme.translations.cart_group_free_quantity_other;
      if (typeof otherTemplate === 'string' && otherTemplate.length > 0) {
        return otherTemplate.replace('{{ count }}', groupState.freeQuantity.toString());
      }

      return (Theme.translations.cart_group_free_quantity || '').replace('{{ count }}', groupState.freeQuantity.toString());
    }

	    if (groupState.hasDiscount) {
	      if (
	        groupState.totalQuantity > 2 &&
	        groupState.discountPercent >= 49 &&
	        groupState.discountPercent <= 51 &&
	        Theme.translations.cart_group_discount_active_50
	      ) {
	        return Theme.translations.cart_group_discount_active_50;
	      }

      return Theme.translations.cart_group_discount_active || '';
    }

	    return '';
	  }

	  #getGroupedDiscountTitles(rows) {
	    const titles = new Set();

	    for (const row of rows) {
	      if (!(row instanceof HTMLElement)) continue;

	      const raw = row.dataset.discountTitles || '';
	      if (!raw) continue;

	      raw
	        .split('||')
	        .map((value) => value.trim())
	        .filter(Boolean)
	        .forEach((title) => titles.add(title));
	    }

	    return Array.from(titles).join(', ');
	  }

  #getRowQuantity(row) {
    return parseInt(row.dataset.quantity || '0', 10) || 0;
  }

  #getRowFinalPrice(row) {
    return parseInt(row.dataset.finalLinePrice || '0', 10) || 0;
  }

  #getRowOriginalPrice(row) {
    return parseInt(row.dataset.originalLinePrice || '0', 10) || 0;
  }

  #isFreeRow(row) {
    return this.#getRowOriginalPrice(row) > 0 && this.#getRowFinalPrice(row) === 0;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
